import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { RelativeTime } from '@/components/RelativeTime';
import { useConfirm } from '@/hooks/useConfirm';
import type { RegionVpn } from '../api';
import { parseList } from '../format';
import { useRegionVpn, useSetRegionVpn, useVpnSessions } from '../hooks';
import { useVpnPermissions } from '../permissions';
import { GatewayVpnBadge, RegionVpnBadge } from './Badges';
import { Card } from './Facts';

function OnOff({ region, liveSessions }: { region: RegionVpn; liveSessions: number }) {
  const { canUpdateVpn } = useVpnPermissions();
  const set = useSetRegionVpn(region.regionId);
  const { confirm, confirmDialog } = useConfirm();
  const able = region.gateways.some((g) => g.vpn.available);
  const blocked = !canUpdateVpn
    ? 'Turning the VPN on or off takes vpn:update'
    : !region.enabled && !able
      ? region.gateways.length === 0
        ? 'This region has no gateway to run the VPN.'
        : 'No gateway in this region can run the VPN yet: each says why below.'
      : undefined;

  const toggle = async (on: boolean) => {
    if (!on) {
      const ok = await confirm({
        title: `Turn the VPN off in ${region.regionName}?`,
        message:
          liveSessions > 0
            ? `Its ${String(liveSessions)} live session${liveSessions === 1 ? '' : 's'} end now, at every gateway here.`
            : 'Nobody can connect to this site until it is turned on again.',
        isDanger: true,
        confirmText: 'Turn off',
      });
      if (!ok) return;
    }
    set.mutate({ enabled: on });
  };

  return (
    <Card
      title="VPN"
      description="Technicians connect to this site's network through any of its gateways that can run the VPN."
      action={<RegionVpnBadge region={region} />}
    >
      <div className="flex items-center gap-3">
        <Switch
          id={`region-vpn-on-${region.regionId}`}
          checked={region.enabled}
          disabled={!!blocked || set.isPending}
          onCheckedChange={(on) => void toggle(on)}
          aria-describedby={blocked ? `region-vpn-blocked-${region.regionId}` : undefined}
        />
        <label htmlFor={`region-vpn-on-${region.regionId}`} className="text-sm">
          {region.enabled ? 'On' : 'Off'}
        </label>
      </div>
      {blocked ? (
        <p id={`region-vpn-blocked-${region.regionId}`} className="text-xs text-muted-foreground mt-2">
          {blocked}
        </p>
      ) : null}
      {confirmDialog}
    </Card>
  );
}

function DnsServers({ region }: { region: RegionVpn }) {
  const { canUpdateVpn } = useVpnPermissions();
  const saved = region.dnsServers.join(', ');
  const [text, setText] = useState(saved);
  const set = useSetRegionVpn(region.regionId);
  const changed = parseList(text).join(', ') !== saved;
  return (
    <Card
      title="Site DNS servers"
      description="Where each gateway's resolver sends the DNS domains. Empty uses each gateway's own DNS servers."
    >
      <div className="flex gap-2">
        <Input
          aria-label="Site DNS servers"
          value={text}
          disabled={!canUpdateVpn}
          title={canUpdateVpn ? undefined : 'Changing them takes vpn:update'}
          placeholder="Each gateway's own"
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
        <Button
          variant="outline"
          disabled={!canUpdateVpn || !changed || set.isPending}
          onClick={() => {
            set.mutate({ enabled: region.enabled, dnsServers: parseList(text) });
          }}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function Routes({ region }: { region: RegionVpn }) {
  const { canUpdateVpn } = useVpnPermissions();
  const saved = region.extraPrefixes.join(', ');
  const [text, setText] = useState(saved);
  const set = useSetRegionVpn(region.regionId);
  const changed = parseList(text).join(', ') !== saved;
  return (
    <Card
      title="Routes"
      description="What a session reaches: the private networks Witina sees on this site's devices, and any you add. A session takes them as they are when it starts."
    >
      {region.routes.length === 0 ? (
        <p className="text-xs text-muted-foreground mb-3">
          None yet: Witina hasn&rsquo;t seen a network here. Add the site&rsquo;s networks below, or sessions can&rsquo;t start.
        </p>
      ) : (
        <ul className="mb-4 space-y-1">
          {region.routes.map((r) => (
            <li key={r.prefix} className="flex items-center gap-2 text-sm">
              <code className="text-xs">{r.prefix}</code>
              <span className="text-xs text-muted-foreground">
                {r.sources.map((s) => (s === 'seen' ? 'seen on its devices' : 'added')).join(' and ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          aria-label="Networks to add"
          value={text}
          disabled={!canUpdateVpn}
          title={canUpdateVpn ? undefined : 'Changing them takes vpn:update'}
          placeholder="192.168.50.0/24, 10.10.0.0/16"
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
        <Button
          variant="outline"
          disabled={!canUpdateVpn || !changed || set.isPending}
          onClick={() => {
            set.mutate({ enabled: region.enabled, extraPrefixes: parseList(text) });
          }}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function Gateways({ region }: { region: RegionVpn }) {
  return (
    <Card title="Gateways" description="Any of them can carry a session; the broker picks one for each.">
      {region.gateways.length === 0 ? (
        <p className="text-xs text-muted-foreground">This region has no gateway.</p>
      ) : (
        <ul className="divide-y divide-border">
          {region.gateways.map((g) => (
            <li key={g.gatewayId} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <Link className="text-sm underline" to={`/devices/${g.gatewayId}?tab=vpn`}>
                  {g.gatewayName ?? g.gatewayId}
                </Link>
                <div className="text-xs text-muted-foreground truncate">
                  {g.vpn.report?.error ??
                    (g.vpn.available ? null : g.vpn.unavailableReason) ??
                    (g.vpn.report ? (
                      <>
                        {String(g.vpn.report.sessions)} session{g.vpn.report.sessions === 1 ? '' : 's'} · reported{' '}
                        <RelativeTime value={g.vpn.report.reportedAt} />
                      </>
                    ) : (
                      'Never reported'
                    ))}
                </div>
              </div>
              <GatewayVpnBadge vpn={g.vpn} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * A region's VPN (docs/VpnAccess.md, "Slice 7 design"): the switch and the
 * site's DNS servers, which every gateway in it follows, and each gateway's
 * state. On the region's page and in the VPN area's Sites.
 */
export function RegionVpnPanel({ regionId }: { regionId: string }) {
  const { data: region, isLoading, isError } = useRegionVpn(regionId);
  const live = useVpnSessions({ regionId, live: true, pageSize: 1 });
  if (isLoading) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (isError || !region) return <p className="text-xs text-muted-foreground">Couldn&rsquo;t load this region&rsquo;s VPN.</p>;
  return (
    <div className="space-y-6">
      <OnOff region={region} liveSessions={live.data?.total ?? 0} />
      {/* Keyed by what's saved, so a save or another viewer's change resets the field. */}
      <DnsServers key={region.dnsServers.join(',')} region={region} />
      <Routes key={region.extraPrefixes.join(',')} region={region} />
      <Gateways region={region} />
    </div>
  );
}

/** The region's VPN on its own page, for those who may see it. */
export function RegionVpnSection({ regionId }: { regionId: string }) {
  const { canReadVpn } = useVpnPermissions();
  return canReadVpn ? <RegionVpnPanel regionId={regionId} /> : null;
}
